import { useEffect, useState } from 'react';
import { apiFetch } from '../auth/session';

interface TenantAnalytics {
  store_id: number;
  period: string;
  kpis: {
    total_events: number;
    reservations: number;
    beauty_bookings: number;
    gym_checkins: number;
    treatments: number;
    classes: number;
    visits: number;
    cancels: number;
    no_shows: number;
    reviews: number;
    coupons_assigned: number;
    coupons_redeemed: number;
    loyalty_points_cents: number;
    usage_billing_cents: number;
  };
  by_vertical: { restaurant: number; beauty: number; gym: number; platform: number };
  by_day: Array<{ date: string; events: number; reservations: number; beauty: number; gym: number }>;
  by_week: Array<{ week: string; events: number }>;
  by_month: Array<{ month: string; events: number }>;
  top_events: Array<{ event_type: string; vertical: string; count: number }>;
  segments: {
    high_frequency: number;
    medium_frequency: number;
    low_frequency: number;
    new_users: number;
    returning_users: number;
  };
  engagement: {
    active_users: number;
    new_users: number;
    recurring_users: number;
    retention_pct: number;
  };
  generated_at: string;
}

type Tab = 'overview' | 'trend' | 'segments' | 'usage';

export function TenantAnalyticsPage() {
  const [data, setData] = useState<TenantAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<Tab>('overview');
  const [days, setDays] = useState(30);

  useEffect(() => {
    setLoading(true);
    setError('');
    apiFetch(`/api/v1/tenant/analytics/summary?days=${days}`)
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.message || `HTTP ${res.status}`);
        }
        return res.json();
      })
      .then((d) => setData(d))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [days]);

  if (loading) return <div className="sk-page"><p>Caricamento analytics…</p></div>;
  if (error) return (
    <div className="sk-page">
      <div className="sk-card">
        <h2 className="sk-card__title">Analytics non disponibile</h2>
        <div className="sk-card__body">
          <p className="sk-error">{error}</p>
          <p className="sk-muted">
            {error.includes('MODULE_NOT_ACTIVE') || error.includes('402')
              ? 'Attiva il modulo Analytics dallo Store per visualizzare i KPI.'
              : 'Riprova più tardi.'}
          </p>
        </div>
      </div>
    </div>
  );
  if (!data) return null;

  const fmtEuro = (cents: number) => `€${(cents / 100).toFixed(2)}`;
  const maxDayEvents = Math.max(...data.by_day.map((d) => d.events), 1);
  const maxWeekEvents = Math.max(...data.by_week.map((d) => d.events), 1);
  const maxMonthEvents = Math.max(...data.by_month.map((d) => d.events), 1);

  return (
    <div className="sk-page">
      <div className="sk-page__header">
        <h1>Analytics</h1>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ fontSize: '0.85rem', padding: '0.25rem 0.5rem' }}>
            <option value={7}>7 giorni</option>
            <option value={30}>30 giorni</option>
            <option value={90}>90 giorni</option>
            <option value={365}>1 anno</option>
          </select>
          <a href={`/api/v1/tenant/analytics/summary?days=${days}&format=csv`} className="sk-btn sk-btn--sm">
            Export CSV
          </a>
        </div>
      </div>

      {/* Tab navigation — blueprint: 4 sezioni */}
      <div className="sk-tabs" style={{ display: 'flex', gap: '0.25rem', marginBottom: '1rem', borderBottom: '2px solid var(--sk-border)' }}>
        {(['overview', 'trend', 'segments', 'usage'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: '0.5rem 1rem',
              border: 'none',
              background: tab === t ? 'var(--sk-primary)' : 'transparent',
              color: tab === t ? '#fff' : 'var(--sk-text)',
              cursor: 'pointer',
              borderRadius: '0.25rem 0.25rem 0 0',
              fontSize: '0.85rem',
              fontWeight: 600,
            }}
          >
            {t === 'overview' ? 'Overview' : t === 'trend' ? 'Trend' : t === 'segments' ? 'Segmenti' : 'Usage billing'}
          </button>
        ))}
      </div>

      {/* === OVERVIEW === */}
      {tab === 'overview' && (
        <div style={{ display: 'grid', gap: '1rem' }}>
          <div className="sk-card">
            <h2 className="sk-card__title">KPI cross-verticale ({data.period})</h2>
            <div className="sk-card__body">
              <div className="sk-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '0.75rem' }}>
                <KpiCard label="Eventi totali" value={data.kpis.total_events} />
                <KpiCard label="Prenotazioni" value={data.kpis.reservations} />
                <KpiCard label="Beauty booking" value={data.kpis.beauty_bookings} />
                <KpiCard label="Check-in gym" value={data.kpis.gym_checkins} />
                <KpiCard label="Trattamenti" value={data.kpis.treatments} />
                <KpiCard label="Corsi" value={data.kpis.classes} />
                <KpiCard label="Visite" value={data.kpis.visits} />
                <KpiCard label="Cancellazioni" value={data.kpis.cancels} />
                <KpiCard label="No-show" value={data.kpis.no_shows} />
                <KpiCard label="Recensioni" value={data.kpis.reviews} />
                <KpiCard label="Coupon assegnati" value={data.kpis.coupons_assigned} />
                <KpiCard label="Coupon riscattati" value={data.kpis.coupons_redeemed} />
                <KpiCard label="Loyalty" value={fmtEuro(data.kpis.loyalty_points_cents)} />
              </div>
            </div>
          </div>

          <div className="sk-card">
            <h2 className="sk-card__title">Per verticale</h2>
            <div className="sk-card__body">
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <VerticalBar label="Restaurant" count={data.by_vertical.restaurant} color="#3b82f6" />
                <VerticalBar label="Beauty" count={data.by_vertical.beauty} color="#ec4899" />
                <VerticalBar label="Gym" count={data.by_vertical.gym} color="#8b5cf6" />
                <VerticalBar label="Platform" count={data.by_vertical.platform} color="#6366f1" />
              </div>
            </div>
          </div>

          <div className="sk-card">
            <h2 className="sk-card__title">Top eventi</h2>
            <div className="sk-card__body">
              <table className="sk-table" style={{ width: '100%', fontSize: '0.85rem' }}>
                <thead>
                  <tr><th style={{ textAlign: 'left' }}>Tipo</th><th style={{ textAlign: 'left' }}>Verticale</th><th style={{ textAlign: 'right' }}>Count</th></tr>
                </thead>
                <tbody>
                  {data.top_events.map((e, i) => (
                    <tr key={i}>
                      <td>{e.event_type}</td>
                      <td>{e.vertical}</td>
                      <td style={{ textAlign: 'right' }}>{e.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* === TREND === */}
      {tab === 'trend' && (
        <div style={{ display: 'grid', gap: '1rem' }}>
          <div className="sk-card">
            <h2 className="sk-card__title">Trend giornaliero</h2>
            <div className="sk-card__body">
              <BarChart data={data.by_day.map((d) => ({ label: d.date.slice(5), value: d.events }))} maxValue={maxDayEvents} />
            </div>
          </div>
          <div className="sk-card">
            <h2 className="sk-card__title">Trend settimanale</h2>
            <div className="sk-card__body">
              <BarChart data={data.by_week.map((d) => ({ label: d.week.slice(5), value: d.events }))} maxValue={maxWeekEvents} />
            </div>
          </div>
          <div className="sk-card">
            <h2 className="sk-card__title">Trend mensile</h2>
            <div className="sk-card__body">
              <BarChart data={data.by_month.map((d) => ({ label: d.month.slice(0, 7), value: d.events }))} maxValue={maxMonthEvents} />
            </div>
          </div>
        </div>
      )}

      {/* === SEGMENTI === */}
      {tab === 'segments' && (
        <div style={{ display: 'grid', gap: '1rem' }}>
          <div className="sk-card">
            <h2 className="sk-card__title">Segmenti end-user</h2>
            <div className="sk-card__body">
              <div className="sk-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.75rem' }}>
                <KpiCard label="Alta frequenza (10+)" value={data.segments.high_frequency} />
                <KpiCard label="Frequenza media (3-9)" value={data.segments.medium_frequency} />
                <KpiCard label="Bassa frequenza (1-2)" value={data.segments.low_frequency} />
                <KpiCard label="Nuovi utenti" value={data.segments.new_users} />
                <KpiCard label="Utenti di ritorno" value={data.segments.returning_users} />
              </div>
            </div>
          </div>
          <div className="sk-card">
            <h2 className="sk-card__title">Engagement</h2>
            <div className="sk-card__body">
              <div className="sk-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.75rem' }}>
                <KpiCard label="Utenti attivi" value={data.engagement.active_users} />
                <KpiCard label="Nuovi utenti" value={data.engagement.new_users} />
                <KpiCard label="Utenti ricorrenti" value={data.engagement.recurring_users} />
                <KpiCard label="Retention %" value={`${data.engagement.retention_pct}%`} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* === USAGE BILLING === */}
      {tab === 'usage' && (
        <div className="sk-card">
          <h2 className="sk-card__title">Usage billing</h2>
          <div className="sk-card__body">
            <div className="sk-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.75rem' }}>
              <KpiCard label="Billing periodo" value={fmtEuro(data.kpis.usage_billing_cents)} />
              <KpiCard label="Loyalty erogata" value={fmtEuro(data.kpis.loyalty_points_cents)} />
              <KpiCard label="Coupon assegnati" value={data.kpis.coupons_assigned} />
              <KpiCard label="Coupon riscattati" value={data.kpis.coupons_redeemed} />
            </div>
            <p className="sk-muted sk-muted--small" style={{ marginTop: '0.75rem' }}>
              Generato: {new Date(data.generated_at).toLocaleString('it-IT')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="sk-stat" style={{ padding: '0.75rem', borderRadius: '0.5rem', background: 'var(--sk-surface)', border: '1px solid var(--sk-border)' }}>
      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--sk-text)' }}>{value}</div>
      <div style={{ fontSize: '0.75rem', color: 'var(--sk-muted)' }}>{label}</div>
    </div>
  );
}

function VerticalBar({ label, count, color }: { label: string; count: number; color: string }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '2rem', fontWeight: 700, color }}>{count}</div>
      <div style={{ fontSize: '0.75rem', color: 'var(--sk-muted)' }}>{label}</div>
    </div>
  );
}

function BarChart({ data, maxValue }: { data: Array<{ label: string; value: number }>; maxValue: number }) {
  if (data.length === 0) return <p className="sk-muted">Nessun dato</p>;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '120px', overflowX: 'auto' }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: '1 0 auto', minWidth: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div
            style={{
              width: '100%',
              height: `${(d.value / maxValue) * 100}%`,
              minHeight: '2px',
              background: 'var(--sk-primary)',
              borderRadius: '2px 2px 0 0',
            }}
            title={`${d.label}: ${d.value}`}
          />
          <div style={{ fontSize: '0.6rem', color: 'var(--sk-muted)', marginTop: '2px', whiteSpace: 'nowrap' }}>{d.label}</div>
        </div>
      ))}
    </div>
  );
}
